import { ArrowRight, Globe } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from 'helpers/cn';
import { BILLING_PLAN_CATALOGUE } from 'definitions/billing-plans';
import { formatBillingDate } from '@/state/billing/billing-format';
import { useBillingState } from '@/state/billing/useBillingState';
import markUrl from '@/public/logo/mark-color.svg';
import { AuthField, AuthHeading, AuthPrimaryButton } from '../Authentication/AuthControls';
import { LegalFooter } from '../Authentication/AuthLayout';
import { PillTabs } from '../Dashboard/ConsolePanel';
import { Snippet } from '../Dashboard/Websites/List/ScriptsModal';
import { StepTitle } from './connect/StepTitle';
import { TipsPanel } from './connect/TipsPanel';
import { useConnectWebsite, type InstallMethod } from './connect/use-connect-website';

const METHOD_OPTIONS = [
  { label: 'Paste it', value: 'paste' },
  { label: 'Tag manager', value: 'gtm' },
] as const;

const GTM_STEPS = [
  'Create a new tag of type Custom HTML.',
  'Paste the snippet and trigger it on All Pages.',
  'Save and publish the container.',
] as const;

/**
 * The step after billing: add the website, then install the snippet, on one page, with the
 * team's tips beside it. Laid out like the auth desk (white page, 10px gutter, two columns
 * that wrap at about 910px), because it is the same family of screen.
 *
 * Installing the snippet is not required to leave: nothing records an installed snippet
 * until an experiment runs, so the page does not pretend to check it.
 */
type ConnectWebsiteStepProps = { isAgency: boolean };

export const ConnectWebsiteStep = ({ isAgency }: ConnectWebsiteStepProps) => {
  const connect = useConnectWebsite({ isAgency });
  const { billingState } = useBillingState();
  const plan = billingState?.plan ? BILLING_PLAN_CATALOGUE[billingState.plan].name : null;
  const trialing = billingState?.subscriptionStatus === 'trialing';

  return (
    <div className="bg-panel flex min-h-screen flex-wrap gap-2.5 p-2.5">
      <main className="flex min-w-0 flex-[1_1_460px] flex-col px-8 pt-[26px] pb-[18px]">
        <div className="mx-auto flex w-full max-w-[480px] flex-1 flex-col justify-center gap-7 py-8">
          <div className="flex items-center justify-between gap-4">
            <img className="h-8 w-auto" src={markUrl} alt="Pertento" />
            <a
              href="/auth/signout"
              className="text-dim hover:text-foreground text-meta underline-offset-4 hover:underline"
            >
              Sign out
            </a>
          </div>

          <div className="flex flex-col items-start gap-3">
            {billingState && (
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="micro-label text-ok border-ok-line bg-ok-bg rounded-full border px-3 py-1.5 whitespace-nowrap">
                  {trialing ? 'Trial started' : 'Plan active'}
                </span>
                {plan && (
                  <span className="micro-label">
                    {plan}
                    {trialing && billingState.trialEnd
                      ? ` · free until ${formatBillingDate(billingState.trialEnd)}`
                      : ''}
                  </span>
                )}
              </div>
            )}
            <AuthHeading
              title="Welcome to Pertento."
              subtitle={
                isAgency
                  ? 'Connect your own website, which is included in the agency fee, and you are ready to run your first test. Clients you add later are billed as their own plans.'
                  : 'Connect your website and you are ready to run your first test.'
              }
            />
          </div>

          <section className="flex flex-col gap-3.5">
            <StepTitle number={1} title="Add your website" state={connect.hasSite ? 'done' : 'current'} />
            {connect.isEditing ? (
              <form
                className="flex flex-col gap-3 pl-8"
                onSubmit={(ev) => {
                  ev.preventDefault();
                  connect.submit();
                }}
              >
                <AuthField
                  label="Website address"
                  name="website"
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  placeholder="https://example.com"
                  value={connect.url}
                  onChange={(ev) => connect.setUrl(ev.target.value)}
                  error={connect.isInvalid ? 'That does not look like a website address.' : undefined}
                  hint="The homepage address, e.g. https://yourstore.com. Add as many websites as you like later."
                />
                <AuthPrimaryButton
                  disabled={!connect.canSubmit}
                  isPending={connect.isSaving}
                  trailingIcon={<ArrowRight className="size-4" strokeWidth={1.5} />}
                >
                  Add website
                </AuthPrimaryButton>
              </form>
            ) : (
              <div className="bg-panel2 border-line ml-8 flex items-center gap-[13px] rounded-xl border px-[13px] py-3">
                <SiteFavicon href={connect.savedHref} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-foreground truncate text-[13px] font-medium">{connect.domain}</span>
                  <span className="log-line text-dim truncate">{connect.savedHref}</span>
                </div>
                <button
                  type="button"
                  onClick={connect.edit}
                  className="text-accent focus-visible:ring-ring cursor-pointer rounded-sm text-micro font-medium focus-visible:ring-2 focus-visible:outline-none"
                >
                  Change
                </button>
              </div>
            )}
          </section>

          <section
            inert={!connect.hasSite}
            className={cn(
              'flex flex-col gap-3.5 transition-opacity duration-200',
              connect.hasSite ? 'opacity-100' : 'opacity-45',
            )}
          >
            <StepTitle number={2} title="Install the snippet" state={connect.hasSite ? 'current' : 'upcoming'} />
            <div className="flex flex-col gap-3 pl-8">
              <p className="text-dim m-0 text-[13px] leading-normal">
                {connect.hasSite
                  ? `Add this line to every page on ${connect.domain} you want to test, just before the closing </head> tag.`
                  : 'Unlocks once your website is added.'}
              </p>
              <div className="whitespace-nowrap">
                <PillTabs
                  options={METHOD_OPTIONS}
                  value={connect.method}
                  onChange={(value) => connect.setMethod(value === 'gtm' ? 'gtm' : ('paste' satisfies InstallMethod))}
                />
              </div>
              {connect.method === 'gtm' && (
                <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
                  {GTM_STEPS.map((step, index) => (
                    <li key={step} className="text-foreground grid grid-cols-[22px_1fr] text-micro leading-normal">
                      <span className="log-line text-dim">{String(index + 1).padStart(2, '0')}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              )}
              <Snippet
                title="Your snippet"
                description="It loads asynchronously and does nothing until you start an experiment."
                snippet={connect.snippet}
                isCopied={connect.isCopied}
                onCopy={connect.copy}
              />
              <span className="text-dim text-nano leading-normal">
                We confirm the install when your first experiment runs. You can find this snippet again under Websites,
                and your developer can add it later.
              </span>
            </div>
          </section>

          <div className="flex flex-col gap-2">
            <AuthPrimaryButton
              type="button"
              disabled={!connect.hasSite}
              onClick={connect.finish}
              trailingIcon={<ArrowRight className="size-4" strokeWidth={1.5} />}
            >
              Go to the dashboard
            </AuthPrimaryButton>
            <span className="text-dim text-center text-nano">
              {connect.hasSite ? 'You can install the snippet later.' : 'Add your website first.'}
            </span>
          </div>
        </div>
      </main>

      <TipsPanel chat={connect.chat} />
      <LegalFooter className="flex basis-full px-3 pt-2 pb-1" />
    </div>
  );
};

type SiteFaviconProps = { href: string };

/**
 * The site's own favicon on a light plate, the way the Websites list and the scope picker draw
 * it: https only, no third-party favicon service (it would learn which sites a customer runs),
 * and a globe when the site has none.
 */
const SiteFavicon = ({ href }: SiteFaviconProps) => (
  <span className="border-line bg-plate flex size-[34px] flex-none items-center justify-center rounded-[9px] border p-1.5">
    <Avatar className="size-full rounded-none">
      {href.startsWith('https') && <AvatarImage src={`${href}favicon.ico`} alt="" className="object-contain" />}
      <AvatarFallback className="bg-transparent">
        <Globe className="text-dim size-4" strokeWidth={1.5} />
      </AvatarFallback>
    </Avatar>
  </span>
);
